/**
 * Readable public URL for a gallery template: `/templates/{brand}/{title}`,
 * e.g. `/templates/bigid/one-week-away-the-ai-infrastructure-summit`.
 *
 * The path (`{brand}/{title}`, no leading slash) is assigned once, when the
 * template first enters the gallery mirror (`convex/templates/public_paths.ts`),
 * and stored on `templatePreviews.publicPath`. It never follows later title
 * edits, so a shared or indexed URL keeps working. The opaque `pt1_` reference
 * stays the internal identity (OG images, remix chips); this is only the URL.
 *
 * Pure and relative-imported so Convex can use it.
 */
import { slugify } from '../shared/text/slugify'
import { registrableDomainLabel } from './brand-name'
import { toPublicTemplateTitle } from './display-title'

/**
 * `/templates/{segment}/…` values another route already owns: the legacy
 * `/templates/email/<reference>` detail route and the `/templates/flows/*`
 * redirect.
 */
const RESERVED_BRAND_SLUGS: ReadonlySet<string> = new Set(['email', 'flows'])

const SLUG_SEGMENT = '[a-z0-9]+(?:-[a-z0-9]+)*'
const PUBLIC_PATH_RE = new RegExp(`^(${SLUG_SEGMENT})/(${SLUG_SEGMENT})$`)

/** Title slugs stay readable in a SERP; the cut never ends on a dash. */
export const TEMPLATE_TITLE_SLUG_MAX_LENGTH = 80

const FALLBACK_BRAND_SLUG = 'brew'
const FALLBACK_TITLE_SLUG = 'email'

/** Straight, curly and modifier apostrophes, plus the backtick stand-in. */
const APOSTROPHES_RE = /['‘’ʼ`]/g

/**
 * Slug with apostrophes dropped rather than turned into a separator, so
 * `Don't` reads `dont`, not `don-t` (`Mom's day` → `moms-day`).
 */
function slugWithoutApostrophes(
  input: string,
  options?: Parameters<typeof slugify>[1]
): string {
  return slugify(input.replace(APOSTROPHES_RE, ''), options)
}

/**
 * The brand segment: the domain's registrable root (`bigid.com` -> `bigid`,
 * `acme.co.uk` -> `acme`). A root that collides with a reserved segment uses
 * the whole domain (`email.com` -> `email-com`). A template without a domain
 * uses its brand's name, then `brew`.
 */
export function templateBrandSlug(input: {
  brandDomain?: string | undefined
  brandName?: string | undefined
}): string {
  const domain = input.brandDomain?.trim()
  if (domain) {
    const root = slugify(registrableDomainLabel(domain) ?? '')
    if (root && !RESERVED_BRAND_SLUGS.has(root)) {
      return root
    }
    const whole = slugify(domain)
    if (whole && !RESERVED_BRAND_SLUGS.has(whole)) {
      return whole
    }
  }
  const name = slugWithoutApostrophes(input.brandName ?? '')
  return name && !RESERVED_BRAND_SLUGS.has(name) ? name : FALLBACK_BRAND_SLUG
}

/** The title segment, from the same public title every surface shows. */
export function templateTitleSlug(rawTitle: string): string {
  return slugWithoutApostrophes(toPublicTemplateTitle(rawTitle), {
    maxLength: TEMPLATE_TITLE_SLUG_MAX_LENGTH,
    fallback: FALLBACK_TITLE_SLUG,
  })
}

/** `{brand}/{title}`: the path a template wants before any `-n` suffix. */
export function templateBasePath(brandSlug: string, titleSlug: string): string {
  return `${brandSlug}/${titleSlug}`
}

/**
 * Whether `publicPath` is `basePath` itself or one of its `-n` siblings —
 * i.e. still what the naming rules would give this template.
 */
export function isPathForBase(publicPath: string, basePath: string): boolean {
  return (
    publicPath === basePath ||
    (publicPath.startsWith(`${basePath}-`) &&
      /^\d+$/.test(publicPath.slice(basePath.length + 1)))
  )
}

/** `-2`, `-3`, … for the n-th template that wants an already-taken path. */
export function withPublicPathSuffix(
  brandSlug: string,
  titleSlug: string,
  attempt: number
): string {
  return attempt <= 1
    ? `${brandSlug}/${titleSlug}`
    : `${brandSlug}/${titleSlug}-${attempt}`
}

/** A well-formed `{brand}/{title}` path, or `null`. */
export function parseTemplatePublicPath(
  value: string
): { brandSlug: string; titleSlug: string } | null {
  const match = PUBLIC_PATH_RE.exec(value)
  if (!(match?.[1] && match[2]) || RESERVED_BRAND_SLUGS.has(match[1])) {
    return null
  }
  return { brandSlug: match[1], titleSlug: match[2] }
}

/**
 * A template's detail-page link: the readable `/templates/{brand}/{title}`
 * (one URL on every surface — the proxy serves signed-out visitors the
 * marketing page behind it), else the opaque reference under `basePath`
 * (private rows, rows not yet given a path).
 */
export function templateDetailHref(
  template: {
    emailId: string
    publicReference?: string | undefined
    publicPath?: string | undefined
  },
  basePath = '/templates'
): string {
  return template.publicPath
    ? `/templates/${template.publicPath}`
    : `${basePath}/email/${template.publicReference ?? template.emailId}`
}
